export const locales = [
  { code: "en", label: "English", dir: "ltr" },
  { code: "ko", label: "한국어", dir: "ltr" },
  { code: "ja", label: "日本語", dir: "ltr" },
  { code: "zh", label: "中文", dir: "ltr" },
  { code: "hi", label: "हिन्दी", dir: "ltr" },
  { code: "de", label: "Deutsch", dir: "ltr" },
  { code: "fr", label: "Français", dir: "ltr" },
  { code: "es", label: "Español", dir: "ltr" },
  { code: "pt", label: "Português", dir: "ltr" },
  { code: "ar", label: "العربية", dir: "rtl" },
] as const;

export type Locale = (typeof locales)[number]["code"];
type Dictionary = Record<string, string>;

const base: Dictionary = {
  product: "Sovasta Friend",
  overview: "Overview",
  hatchery: "Profile",
  growth: "Resonance",
  skills: "Matches",
  pricing: "Pricing",
  support: "Support",
  account: "Account",
  install: "Install app",
  signin: "Sign in",
  heroKicker: "AFFECTIVE AI · RESONANCE COACH",
  heroTitle: "Feel before you swipe.",
  heroBody:
    "Sovasta Friend turns emotional state, values, and conversation goals into a safer relationship map. Practice with an AI coach, discover high-resonance matches, and keep human approval at the center.",
  heroPrimary: "Build my resonance profile",
  heroSecondary: "See ERS map",
  socialProof: "6D emotion vector · ERS score · Human decision gate",
  demoTitle: "Live resonance preview",
  demoName: "Warm Explorer · ERS 0.82",
  demoMood: "Signal: high curiosity, low pressure",
  demoLine1: "AI coach suggests a first message that does not feel generic.",
  demoLine2: "Proxy report explains why a match is emotionally compatible.",
  demoLine3: "You approve, pause, or delete every relationship recommendation.",
  onboardingTitle: "Four short scenes before you start",
  onboardingSubtitle: "Text-first onboarding disappears after the preview or when you close it.",
  scene1: "1. Map your current state",
  scene1Body: "A short check-in turns mood, energy, values, and relationship intent into a private 6D profile.",
  scene2: "2. Practice safely",
  scene2Body: "Try messages with an AI coach before sending them to a real person.",
  scene3: "3. Discover resonance",
  scene3Body: "ERS shows where emotional timing, values, and conversation style actually line up.",
  scene4: "4. Keep the human gate",
  scene4Body: "AI can recommend. You decide. Sensitive relationships never move without your approval.",
  close: "Close",
  hatchTitle: "Create a private emotional profile first.",
  hatchBody: "Guest mode stores the profile on this device. Sign in only when you save, sync, or activate matching.",
  companionName: "Display name",
  purpose: "Relationship goal",
  boundary: "Safety boundary",
  generate: "Generate ERS preview",
  save: "Save with One Sovasta ID",
  draftCard: "Local resonance profile",
  growthTitle: "Your resonance map should feel alive.",
  growthBody: "Track emotional readiness, conversation safety, and match quality before the platform asks you to pay or meet.",
  statInt: "Clarity",
  statEmp: "Warmth",
  statMem: "Memory",
  statAut: "Agency",
  statTrust: "Safety",
  level: "ERS",
  streak: "Check-in streak",
  nextUnlock: "Next coaching mode",
  skillsTitle: "Matches, AI companions, and proxy reports stay permission-based.",
  skillsBody: "Sovasta Friend supports human-to-human discovery, human-to-AI practice, and AI proxy reports without pretending AI has final authority.",
  skillCare: "Human match",
  skillCareBody: "Emotional resonance score, values alignment, and safer first-message coaching.",
  skillWork: "AI companion",
  skillWorkBody: "Private practice space for loneliness, conflict rehearsal, and daily emotional reflection.",
  skillSocial: "Proxy report",
  skillSocialBody: "Your AI proxy can explore compatibility and bring back a plain-language report.",
  skillTrust: "Safety gate",
  skillTrustBody: "Boundaries, consent reminders, abuse-risk prompts, and delete-anytime memory controls.",
  pricingTitle: "Pricing that follows trust and depth, not swipes.",
  pricingBody: "Sovasta Friend is included in Sovasta, a single subscription covering every Sovasta solution. Manage your plan on Sovasta Hub.",
  planFree: "Free",
  planGrow: "Resonance+",
  planFamily: "Resonance Pro",
  planStudio: "Resonance Soul",
  choose: "Choose plan",
  freeFeature: "5 daily checks, basic ERS, guest profile",
  growFeature: "Unlimited coaching and detailed ERS analysis",
  familyFeature: "Proxy reports, relationship reviews, events",
  studioFeature: "VIP matching, 1:1 AI relationship coach",
  supportTitle: "A clear next step whenever the relationship feels unclear.",
  supportBody: "Ask the AI guide for product help, safety guidance, or conversation coaching. Contact a person for account, privacy, or billing issues.",
  askGuide: "Ask the AI guide",
  contactPerson: "Contact a person",
  message: "Message",
  send: "Send",
  subject: "Subject",
  faqTitle: "Frequently asked questions",
  faq1: "Is Sovasta Friend a dating app?",
  faq1Body: "It can support dating, friendship, and AI practice, but it is designed around emotional safety and consent rather than endless swiping.",
  faq2: "Does AI decide who I meet?",
  faq2Body: "No. AI can score resonance and explain why, but the human decision gate stays in your hands.",
  faq3: "Can I delete emotional data?",
  faq3Body: "Yes. Guest data is local, and synced profile data should be reviewable, pausable, and deletable.",
  accountTitle: "Guest mode is active.",
  accountBody: "Your profile stays in this browser. Sign in to sync your emotional profile, manage Stripe billing, and activate matching.",
  terms: "Terms",
  privacy: "Privacy",
  footer: "Emotional resonance, AI coaching, and safe relationship discovery for the Sovasta ecosystem.",
  community: "Community",
  signout: "Sign out",
  signinModalTitle: "Sign in",
  signinModalBody: "Sign in with One Sovasta ID to sync your resonance profile, post in Community, and manage billing.",
  communityEyebrow: "CUSTOMER VOICE",
  communityTitle: "Community",
  communityBody: "Tell us what Sovasta Friend still needs, see what other members are asking for, and find Sovasta Friend discussed in the wider community.",
  communityWallTitle: "The member wall",
  communityWallGate: "Sign in to read and post on the member wall. Every post is tied to a real Sovasta ID — nothing here is seeded.",
  communityShareLabel: "What should Sovasta Friend do next?",
  communityTopicLabel: "Topic",
  communityTitleLabel: "Short summary",
  communityPost: "Post to the member wall",
  communityNeedMore: "Say a bit more before posting.",
  communityPostSent: "Posted to the member wall.",
  communityPostError: "Couldn't reach the member wall right now.",
  communityEmpty: "No member posts yet — be the first to share what you need.",
  communityReplies: "replies",
  communityBack: "Back to Community",
  communityNoReplies: "No replies yet.",
  communityYourReply: "Your reply",
  communityPostReply: "Post reply",
  catGeneral: "General",
  catHatchery: "Profile & practice",
  catGrowth: "Resonance & matches",
  catBilling: "Billing & pricing",
  externalTitle: "Discuss in the open",
  externalText: "Real, live search results — Sovasta Friend doesn't embed Reddit or Quora because neither platform allows third-party embedding.",
  startRedditPost: "Start a Reddit post",
  joinSubreddit: "Browse r/dating_advice",
  exploreQuora: "Explore AI relationship coaching on Quora",
  externalDisclosure: "Reddit and Quora are independent third-party services Sovasta Friend doesn't moderate or control.",
  consentTitle: "Before you post publicly",
  consentText: "Your post and replies are visible to every other signed-in Sovasta Friend member, alongside the email on your account.",
  consentCheckbox: "I understand my post is visible to other members and agree to the Community rules in the Terms.",
  consentAgree: "Agree and post",
  guideHeroEyebrow: "FEATURE GUIDE",
  guideHeroTitle: "See everything Sovasta Friend can do for you",
  guideHeroCta: "Show me →",
  guideHeroDone: "You've explored it all — here's a refresher",
};

const dictionaries: Record<Locale, Dictionary> = {
  en: base,
  ko: {
    ...base,
    overview: "개요", hatchery: "프로필", growth: "공명", skills: "매칭", pricing: "가격", support: "지원", account: "계정", install: "앱 설치", signin: "로그인",
    heroKicker: "감정 AI · 공명 코치",
    heroTitle: "스와이프하기 전에 먼저 느껴보세요.",
    heroBody: "Sovasta Friend는 감정 상태, 가치관, 관계 목표를 안전한 관계 지도로 바꿉니다. AI 코치로 연습하고, 공명도가 높은 연결을 발견하며, 최종 결정은 언제나 사람이 합니다.",
    heroPrimary: "내 공명 프로필 만들기",
    heroSecondary: "ERS 지도 보기",
    supportTitle: "관계가 불확실할 때 다음 행동을 명확히 알려드립니다.",
  },
  ja: { ...base, overview: "概要", hatchery: "プロフィール", growth: "共鳴", skills: "マッチ", pricing: "料金", support: "サポート", account: "アカウント", install: "アプリをインストール", signin: "サインイン", heroTitle: "スワイプの前に、まず感じる。", heroBody: "感情状態、価値観、会話目標を安全な関係マップに変えます。" },
  zh: { ...base, overview: "概览", hatchery: "资料", growth: "共鸣", skills: "匹配", pricing: "价格", support: "支持", account: "账户", install: "安装应用", signin: "登录", heroTitle: "滑动之前，先感受。", heroBody: "把情绪状态、价值观和关系目标转化为更安全的关系地图。" },
  hi: { ...base, overview: "सारांश", hatchery: "प्रोफ़ाइल", growth: "रेज़ोनेंस", skills: "मैच", pricing: "मूल्य", support: "सहायता", account: "खाता", install: "ऐप इंस्टॉल करें", signin: "साइन इन", heroTitle: "स्वाइप करने से पहले महसूस करें।", heroBody: "Sovasta Friend भावनात्मक स्थिति, मूल्यों और बातचीत के लक्ष्यों को सुरक्षित संबंध मानचित्र में बदलता है।" },
  de: { ...base, overview: "Überblick", hatchery: "Profil", growth: "Resonanz", skills: "Matches", pricing: "Preise", support: "Support", account: "Konto", install: "App installieren", signin: "Anmelden", heroTitle: "Fühle, bevor du swipest.", heroBody: "Sovasta Friend macht emotionale Zustände, Werte und Gesprächsziele zu einer sicheren Beziehungskarte." },
  fr: { ...base, overview: "Aperçu", hatchery: "Profil", growth: "Résonance", skills: "Rencontres", pricing: "Tarifs", support: "Assistance", account: "Compte", install: "Installer l’app", signin: "Connexion", heroTitle: "Ressentez avant de swiper.", heroBody: "Sovasta Friend transforme l’état émotionnel, les valeurs et les objectifs relationnels en carte de relation plus sûre." },
  es: { ...base, overview: "Resumen", hatchery: "Perfil", growth: "Resonancia", skills: "Conexiones", pricing: "Precios", support: "Soporte", account: "Cuenta", install: "Instalar app", signin: "Iniciar sesión", heroTitle: "Siente antes de deslizar.", heroBody: "Sovasta Friend convierte emoción, valores y metas de conversación en un mapa relacional más seguro." },
  pt: { ...base, overview: "Visão geral", hatchery: "Perfil", growth: "Ressonância", skills: "Conexões", pricing: "Preços", support: "Suporte", account: "Conta", install: "Instalar app", signin: "Entrar", heroTitle: "Sinta antes de deslizar.", heroBody: "Sovasta Friend transforma emoção, valores e metas de conversa em um mapa de relação mais seguro." },
  ar: { ...base, overview: "نظرة عامة", hatchery: "الملف", growth: "الرنين", skills: "المطابقات", pricing: "الأسعار", support: "الدعم", account: "الحساب", install: "تثبيت التطبيق", signin: "تسجيل الدخول", heroTitle: "اشعر قبل أن تمرر.", heroBody: "يحوّل Sovasta Friend الحالة العاطفية والقيم وأهداف الحديث إلى خريطة علاقات أكثر أماناً." },
};

export function getDictionary(locale: Locale) {
  return dictionaries[locale] || dictionaries.en;
}

export function localeDir(locale: Locale) {
  return locales.find((item) => item.code === locale)?.dir || "ltr";
}
